/**
 * [MY-PERF 2026-09-02] My Performance — one project manager's whole record, read top to bottom.
 *
 * Built for two readers at once: a manager who wants the verdict in one glance, and someone on
 * their first week who has never heard the words "inventory" or "retention". So the page opens
 * with the numbers, explains the three words right underneath them, then tells the story month by
 * month. The server still sends `provenance` (where every number comes from); the page no longer
 * renders it — Section 02's glossary carries the understanding instead.
 *
 * Every figure is served by server/services/myPerformance.ts — nothing is computed here beyond
 * formatting and chart geometry — so this page cannot disagree with the rest of the app by
 * construction. Charts are hand-rolled SVG like every other chart in the app (see
 * ProcessAnalysisPage): `viewBox` + `width="100%"` + `preserveAspectRatio="xMidYMid meet"`, so the
 * rendered height scales with the width and text stays legible at any size.
 *
 * What is deliberately NOT here: team roll-ups, peer comparison, rankings — the user asked for one
 * PM at a time. The current month is not shown either: its payments are still arriving.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { lgTile, lgSquircle, lgRim, lgControlBg, lgCardSheen } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02, user]
import {
  fetchMyPerformance, MyPerfData, MyPerfMonth,
} from '../services/api';
import { SkeletonBox } from '../components/Skeleton';
import { LoadFailed, isForbidden } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import GlassSelect from '../components/GlassSelect';
// [ONE GLASS RECIPE 2026-09-01, user] One tooltip surface for the whole app — see TipGlass.
import TipGlass from '../components/TipGlass';
import { useIsMobile } from '../hooks/useIsMobile'; // [PHONE-LAYOUT 2026-10-23] the charts' phone width
import { layoutMax } from '../utils/appZoom'; // [APP-FIT 2026-10-26] laptop width rules follow the laid-out width

/* ── formatting ─────────────────────────────────────────────────────────────────────────── */
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtMoney = (n: number | null | undefined) => (n == null ? '—' : `$${Math.round(n).toLocaleString('en-US')}`);
const fmtPct = (n: number | null | undefined, d = 1) => (n == null ? '—' : `${n.toFixed(d)}%`);
const fmtMonth = (ym: string | null | undefined) => {
  if (!ym) return '—';
  const [y, m] = ym.split('-');
  return `${MONTHS_SHORT[Number(m) - 1] || m} ${y}`;
};
const fmtMonthTick = (ym: string) => {
  const [y, m] = ym.split('-');
  return `${MONTHS_SHORT[Number(m) - 1] || m} ’${y.slice(2)}`;
};
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/* [PLAIN-ENGLISH 2026-09-09, user] Money banked per $1 managed, IN THE NARRATIVE PANELS ONLY.
   Under a dollar it stays in cents ("95c"); a dollar or more reads as money ("$1.13"), because
   "113c" makes the reader do the conversion in their head mid-sentence. The KPI tile and the
   month table keep the raw ¢ figure on purpose — a column mixing "95¢" with "$1.13" would not
   line up, and those are read as a column rather than as a sentence. */
const perDollar = (cents: number) => (cents >= 100 ? `$${(cents / 100).toFixed(2)}` : `${Math.round(cents)}c`);
/* [MY-PERF 2026-09-02, user] Upsell split in words, naming ONLY the parts that exist: "$500 one
   time and $200 recurring", or just one of them, or nothing at all when there was no upsell —
   "if any one from one time or recurring is blank dont show the text there". */
const upsellSplit = (oneTime: number, recurring: number): string => {
  const parts: string[] = [];
  if (oneTime > 0) parts.push(`${fmtMoney(oneTime)} One Time Upsell`);
  if (recurring > 0) parts.push(`${fmtMoney(recurring)} Recurring Upsell`);
  return parts.join(' and ');
};

/* Shared with Process Analysis so a colour means the same thing on every tab. ⚠ The CHART's
   colours are NOT here — they live in useInk() below, because green, red and amber each need a
   different value per theme (the light green #1a7f64 goes muddy on the dark card). */
const AMBER = '#c98a1b';
const RED = '#c0392b';
const BLUE = '#3b82f6';
const greenInk = (p: any) => p.theme.greenInk as string;
const greenTint = (p: any) => p.theme.greenTint as string;

/* ── layout (same bones as the other report pages so the tab reads as one app) ──────────── */
const Container = styled.div`
  background: ${(p) => p.theme.surface};
  padding: 14px 20px 140px;
  transition: background 320ms cubic-bezier(0.4, 0, 0.2, 1);
  /* [PHONE-LAYOUT 2026-10-23] the bottom was 8 px: the page's last line ("The page runs from …") ended under the dock;
     96 px lifts it above (the card's own end padding is not scrolled into view) */
  @media (max-width: 768px) { padding: 12px 12px 96px; }
`;
const PageHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding-bottom: 14px;
  border-bottom: 1px solid ${(p) => p.theme.line};
  margin-bottom: 20px;

  /* [MY-PERF PHONE HEAD CENTRE 2026-09-23, user] "make all the data come in the middle": phones
     stack the head and centre everything in it: title, description, then the range pill + PM
     picker (HeadRight). Desktop keeps text left, controls right. */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;
  }
`;
/* [MY-PERF 2026-09-02, user] Title and description on ONE line, divided by a rule — the header
   was two stacked blocks and the user asked for a single line. Wraps on a narrow screen so the
   description drops under the title rather than squeezing the controls. */
const TitleLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;

  /* [MY-PERF PHONE HEAD CENTRE 2026-09-23, user] Phones: title over description, both centred.
     The divider rule (the only span child here) cannot sit between two stacked lines, so it is
     hidden in THIS head only. The section heads' own rules keep their 640px breakpoint. A child
     selector, not the TitleRule component: TitleRule is declared below this, and referencing it
     here would throw at module load. */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 6px;
    > span { display: none; }
  }
`;
const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.3px;
  color: ${(p) => p.theme.text};
  margin: 0;
  white-space: nowrap;
`;
const TitleRule = styled.span`
  width: 1px;
  align-self: stretch;
  min-height: 16px;
  background: ${(p) => p.theme.line};
  @media (max-width: 640px) { display: none; }
`;
const PageSub = styled.p`
  font-size: 12.5px;
  color: ${(p) => p.theme.muted};
  margin: 0;
  line-height: 1.5;
`;
const HeadRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  /* [MY-PERF PHONE HEAD CENTRE 2026-09-23, user] Phones: range pill + PM picker centred together;
     on a very narrow phone they wrap onto two centred lines. */
  @media (max-width: 768px) {
    justify-content: center;
  }
`;
/* [LIQUID-GLASS 2026-11-02] a glass pill (a shine, a light rim); light keeps a thin edge on the white page */
const WindowPill = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 13px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'transparent' : 'rgba(22,25,23,0.12)')};
  background-color: ${lgControlBg};
  background-image: ${lgCardSheen};
  box-shadow: ${(p) => (p.theme.colorScheme === 'dark' ? '0 1px 1px rgba(0,0,0,0.25), 0 8px 18px -10px rgba(0,0,0,0.6)' : '0 1px 2px rgba(22,25,23,0.06), 0 6px 14px -8px rgba(22,25,23,0.2)')};
  ${lgRim}
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  white-space: nowrap;
`;
/* [MY-PERF 2026-09-02, user] NO min-width here. GlassSelect's trigger is inline-flex with its own
   min-width: 130px — it hugs its label rather than filling its parent, so a 220px wrapper left a
   visible strip of dead space to the right of the picker ("why the blank space there"). Hugging
   the trigger puts the control flush against the right edge. */
const PickWrap = styled.div`
  display: inline-flex;
`;
const Section = styled.section`
  margin-bottom: 28px;
`;
const Eyebrow = styled.div`
  font-size: 10.5px;
  @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-23] the smallest label size on a phone */
  font-weight: 800;
  letter-spacing: 0.9px;
  text-transform: uppercase;
  color: ${greenInk};
  margin-bottom: 3px;
`;
const SectionTitle = styled.h2`
  font-size: 15.5px;
  font-weight: 750;
  color: ${(p) => p.theme.text};
  margin: 0 0 3px;
`;
const SectionSub = styled.p`
  font-size: 12px;
  color: ${(p) => p.theme.muted};
  margin: 0 0 12px;
  line-height: 1.5;
  max-width: 900px;
`;
/* [MY-PERF 2026-09-02, user] A section's title and its one-line description on the SAME line,
   divided by a rule — the page header treatment applied to a section. Only for descriptions short
   enough to sit beside the title; the longer explanatory ones stay on their own line below. */
const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  /* The 900px cap on SectionSub is what makes a one-line head wrap — lift it here only. */
  > h2, > p { margin: 0; }
  > p { max-width: none; }
`;
const Card = styled.div<{ $plain?: boolean }>`
  ${lgSquircle(14)}
  ${lgTile} /* [LIQUID-GLASS 2026-11-02] glass cards (the tiles too); the numbers and charts stay as they are */
  /* [LIQUID-GLASS 2026-11-02, user] "make this same as we have before": $plain = the old card (the month table's) */
  ${(p) => (p.$plain ? `border: 1px solid ${p.theme.line}; border-radius: 14px; corner-shape: round; background: ${p.theme.surface}; box-shadow: none;` : '')}
  padding: 14px 16px 16px;
  min-width: 0;
`;
const Grid = styled.div<{ $min?: number }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${(p) => p.$min ?? 170}px, 1fr));
  gap: 10px;
`;
const Tile = styled(Card)`
  padding: 12px 14px 13px;
`;
const TileLabel = styled.div`
  font-size: 10px;
  @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-23] the smallest label size on a phone */
  font-weight: 750;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${(p) => p.theme.muted};
  margin-bottom: 6px;
  white-space: nowrap;
`;
const TileValue = styled.div<{ $tone?: 'good' | 'bad' | 'plain' }>`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.4px;
  line-height: 1.1;
  color: ${(p) => (p.$tone === 'good' ? greenInk(p) : p.$tone === 'bad' ? RED : p.theme.text)};
`;
const TileSub = styled.div`
  font-size: 11px;
  color: ${(p) => p.theme.muted};
  margin-top: 5px;
  line-height: 1.4;
`;
const Scroll = styled.div`
  overflow-x: auto;
  max-width: 100%;
`;
const ChartScroll = styled.div`
  overflow-x: auto;
  max-width: 100%;
`;
const Legend = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 11.5px;
  color: ${(p) => p.theme.muted};
  margin: 2px 0 8px;
  span { display: inline-flex; align-items: center; gap: 6px; }
  i { display: inline-block; width: 12px; height: 12px; border-radius: 3px; }
  i.line { height: 3px; border-radius: 2px; }
`;
/* [MY-PERF 2026-09-02, user] The one-line read-out under the chart's legend: "a summary line
   which show the summary data for the graph ... in simple language and not too big". Centred,
   quiet, and only ever built from figures already plotted above it. */
/* [MY-PERF 2026-09-02, user] Chart on the left, the reading of it on the right — the "what this
   shows" column the written performance reviews use, which the user asked for because the tab
   "is looking a little flat". 80/20 on a wide screen; the panel drops UNDER the chart below
   1200px, where a fifth of the width is too narrow for prose. */
const SplitRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 4fr) minmax(260px, 1fr);
  gap: 16px;
  align-items: stretch;
  ${layoutMax(1200)} { grid-template-columns: minmax(0, 1fr); }
`;
const Insight = styled.aside`
  ${lgSquircle(12)}
  ${lgTile} /* [LIQUID-GLASS 2026-11-02] a soft glass note */
  padding: 14px 15px 15px;
  min-width: 0;

  h4 {
    margin: 0 0 9px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.7px;
    text-transform: uppercase;
    color: ${greenInk};
  }
  p {
    margin: 0 0 10px;
    font-size: 12px;
    line-height: 1.6;
    color: ${(p) => p.theme.text};
  }
  p:last-child { margin-bottom: 0; }
  b { font-weight: 750; }
  em { font-style: normal; color: ${(p) => p.theme.muted}; }
`;
/* [MY-PERF 2026-09-02, user] The tiles say WHAT; this says what it MEANS — the reading the
   written performance reviews carry, but measured against the company line for the same month
   rather than left as a bare figure. Full width under the tiles, quiet enough not to compete
   with them. */
/* [MY-PERF 2026-09-02, user] A key above the month table. The two columns that carry meaning in
   their INK rather than their value — Retention % and the arrow on Average Retention % — are the
   only ones a reader has to be told how to read, so those are the only ones it explains. */
const TableKey = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  align-items: center;
  margin: 0 0 11px;
  font-size: 11.5px;
  line-height: 1.5;
  color: ${(p) => p.theme.text};

  span { display: inline-flex; align-items: center; gap: 5px; }
  b { font-weight: 750; }

  /* [PHONE-LAYOUT 2026-10-23] phones: each key item reads as ordinary text — as inline-flex the long "Everyone else %"
     sentence put every word of it in a column of its own, one word a line */
  @media (max-width: 768px) { span { display: inline; } }
`;
/* [MY-PERF 2026-09-02, user] The change against the SAME MONTH A YEAR EARLIER, sat under a tile.
   Same month, not the previous one: a book that always dips in July should not read as decline
   every July. Hidden entirely when there is no year-ago month rather than shown as zero. */
const Delta = styled.span<{ $tone: 'good' | 'bad' | 'flat' }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 7px;
  font-size: 11px;
  font-weight: 750;
  white-space: nowrap;
  color: ${(p) => (p.$tone === 'good' ? p.theme.greenInk
    : p.$tone === 'bad' ? (p.theme.colorScheme === 'dark' ? '#f87171' : RED)
      : p.theme.muted)};
`;
/* [MY-PERF 2026-09-02, user] The glossary as a tight two-column list rather than five cards.
   It was "the weakest thing on the page" — a full row of static boxes a returning reader
   scrolls past every time. Same five definitions, a third of the height, still readable.
   The TERM stays in green so the eye can find it; the definition is ordinary ink. */
const Terms = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(330px, 1fr));
  gap: 7px 26px;
  margin: 0;

  div {
    display: flex;
    gap: 7px;
    align-items: baseline;
    padding: 5px 0;
    border-top: 1px solid ${(p) => p.theme.line};
  }
  dt {
    flex: 0 0 auto;
    font-size: 12px;
    font-weight: 800;
    color: ${(p) => p.theme.greenInk};
    white-space: nowrap;
  }
  dd {
    margin: 0;
    font-size: 12px;
    line-height: 1.55;
    color: ${(p) => p.theme.text};
  }

  /* [PHONE-LAYOUT 2026-10-23] phones: one column — the 330 px column floor is wider than a 360 px phone's card, which
     made the whole page slide sideways */
  @media (max-width: 768px) { grid-template-columns: minmax(0, 1fr); }
`;
/* [MY-PERF 2026-09-02, user] Year against year, the spine of the written reviews. Deliberately
   LIKE FOR LIKE: only the months present in the current year are taken from the year before, so
   a part-finished year is never compared with a full one. */
const YoY = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;

  th, td { padding: 8px 10px; border-bottom: 1px solid ${(p) => p.theme.line}; }
  th {
    text-align: right;
    font-size: 10.5px;
    @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-23] the smallest label size on a phone */
    font-weight: 800;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${(p) => p.theme.muted};
    white-space: nowrap;
  }
  th:first-child, td:first-child { text-align: left; }
  td { text-align: right; color: ${(p) => p.theme.text}; }
  td:first-child { font-weight: 700; }
  tr:last-child td { border-bottom: 0; }
`;
/* [MY-PERF 2026-09-02, user] The closing read: what is strong, what to watch. Two plain columns,
   green and red headings, short bullets. ⚠ Every line is produced by a THRESHOLD ON A NUMBER
   already shown elsewhere on this page — never an opinion, and never a claim the page cannot
   evidence. If a test has no data behind it, its bullet simply does not appear. */
const Verdict = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 14px 26px;

  h4 {
    margin: 0 0 8px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.7px;
    text-transform: uppercase;
  }
  h4.good { color: ${(p) => p.theme.greenInk}; }
  h4.bad { color: ${(p) => (p.theme.colorScheme === 'dark' ? '#f87171' : RED)}; }
  ul { margin: 0; padding-left: 17px; }
  li { margin: 0 0 6px; font-size: 12.5px; line-height: 1.6; color: ${(p) => p.theme.text}; }
  b { font-weight: 750; }
  p.none { margin: 0; font-size: 12.5px; color: ${(p) => p.theme.muted}; }

  /* [PHONE-LAYOUT 2026-10-23] phones: one column — the 320 px column floor is wider than a 360 px phone's card, which
     made the whole page slide sideways */
  @media (max-width: 768px) { grid-template-columns: minmax(0, 1fr); }
`;

/* A short, plain statement of where the figures come from. The long version was removed for being
   "too heavy in terms of texts"; this keeps the part that earns trust and drops the rest. */
const Method = styled.ul`
  margin: 0;
  padding-left: 17px;
  columns: 2;
  column-gap: 30px;

  li {
    margin: 0 0 6px;
    font-size: 12px;
    line-height: 1.6;
    color: ${(p) => p.theme.text};
    break-inside: avoid;
  }
  b { font-weight: 750; }
  ${layoutMax(900)} { columns: 1; }
`;
const Standing = styled.div`
  margin-top: 12px;
  ${lgSquircle(12)}
  ${lgTile} /* [LIQUID-GLASS 2026-11-02] a soft glass note */
  padding: 13px 16px 14px;

  h4 {
    margin: 0 0 8px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.7px;
    text-transform: uppercase;
    color: ${(p) => p.theme.greenInk};
  }
  p {
    margin: 0 0 8px;
    font-size: 12.5px;
    line-height: 1.65;
    color: ${(p) => p.theme.text};
  }
  p:last-child { margin-bottom: 0; }
  b { font-weight: 750; }
  em { font-style: normal; color: ${(p) => p.theme.muted}; }
`;
const ChartSummary = styled.p`
  margin: 4px auto 0;
  padding-top: 10px;
  border-top: 1px solid ${(p) => p.theme.line};
  max-width: none;
  text-align: center;
  font-size: 12px;
  line-height: 1.55;
  color: ${(p) => p.theme.text};
  b { font-weight: 750; }
`;
/* [MY-PERF PHONE SCROLL 2026-09-23, user] overflow-x auto, NOT hidden. Hidden clipped the 14-column
   month table INSIDE this panel, so the Scroll wrapper around it never saw anything wider than
   itself and had nothing to scroll — on a phone the table stopped at Retention %. The panel now
   scrolls sideways itself; the rounded corners still clip. */
/* [LIQUID-GLASS 2026-11-02, user] "make this same as we have before": the month table keeps its old frame (no glass) */
const TablePanel = styled.div`
  border-radius: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.62)')};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')};
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`;
const Th = styled.th<{ $right?: boolean }>`
  text-align: ${(p) => (p.$right ? 'right' : 'left')};
  font-size: 10px;
  @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-23] the smallest label size on a phone */
  font-weight: 750;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${(p) => p.theme.muted};
  padding: 10px 10px 8px;
  border-bottom: 1px solid ${(p) => p.theme.line};
  white-space: nowrap;
`;
const Td = styled.td<{ $right?: boolean; $strong?: boolean; $muted?: boolean; $wrap?: boolean }>`
  text-align: ${(p) => (p.$right ? 'right' : 'left')};
  padding: 7px 10px;
  color: ${(p) => (p.$muted ? p.theme.muted : p.theme.text)};
  font-weight: ${(p) => (p.$strong ? 700 : 500)};
  border-bottom: 1px solid ${(p) => p.theme.line};
  white-space: ${(p) => (p.$wrap ? 'normal' : 'nowrap')};
  vertical-align: top;
  line-height: 1.45;
`;
/* [MY-PERF 2026-09-02, user] Figures in the month table carry their meaning in the INK, not in a
   pill — "just show the numbers in colours". Retention uses the same thresholds as the chart
   labels; money out is red and money kept is green. */
/* [MY-PERF 2026-09-02, user] Data bars behind the Inventory and Retained figures, the same
   device the written performance reviews use ("bars are scaled within each column"): the
   biggest month in a column is the full width and every other cell is drawn against it, so a
   reader sees the shape of the two years without reading a single number.
   ⚠ Scaled PER COLUMN, not across both — retained is compared with the best retained month,
   not with inventory. Retention against inventory is its own column two cells to the right,
   and overlaying the two scales here would quietly invent a second, different reading of it. */
const BarCell = styled.span<{ $pct: number; $tone: 'blue' | 'green' }>`
  position: relative;
  display: block;
  padding: 3px 7px;
  border-radius: 5px;
  overflow: hidden;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: ${(p) => Math.max(0, Math.min(100, p.$pct))}%;
    border-radius: 5px;
    z-index: -1;
    background: ${(p) => (p.$tone === 'green'
      ? (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.22)' : 'rgba(26,127,100,0.15)')
      : (p.theme.colorScheme === 'dark' ? 'rgba(96,165,250,0.24)' : 'rgba(37,99,235,0.13)'))};
  }
`;
const ToneText = styled.span<{ $tone: 'good' | 'warn' | 'bad' | 'muted' }>`
  font-weight: 750;
  color: ${(p) => (p.$tone === 'good' ? p.theme.greenInk
    : p.$tone === 'bad' ? (p.theme.colorScheme === 'dark' ? '#f87171' : RED)
      : p.$tone === 'warn' ? (p.theme.colorScheme === 'dark' ? '#fbbf24' : AMBER)
        : p.theme.muted)};
`;
const Small = styled.span`
  font-size: 11px;
  color: ${(p) => p.theme.muted};
`;
const Empty = styled.div`
  padding: 26px 8px;
  text-align: center;
  font-size: 12px;
  color: ${(p) => p.theme.muted};
`;
const ErrorBox = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: ${(p) => p.theme.danger};
  font-size: 13px;
  font-weight: 600;
`;

/* ── chart ─────────────────────────────────────────────────────────────────────────────── */
/**
 * Bars for money per month, an optional second bar stacked on top, and an optional line (a
 * percentage) on its own right-hand scale. `gaps[i]` blanks a month: no bar, and the line is
 * BROKEN there rather than drawn across it — a month with no clients is not a month at 0%.
 */
const MonthChart: React.FC<{
  months: string[];
  bars: number[];
  bars2?: number[];
  line?: (number | null)[];
  gaps: boolean[];
  barLabel: string;
  bar2Label?: string;
  lineLabel?: string;
  barColor: string;
  bar2Color?: string;
  lineColor?: string;
  tip: (i: number) => { label: string; value: string }[];
}> = ({ months, bars, bars2, line, gaps, barLabel, bar2Label, lineLabel, barColor, bar2Color, lineColor, tip }) => {
  const theme = useTheme() as any;
  const chartInk = useInk();
  const [hover, setHover] = useState<number | null>(null);
  /* [PHONE-LAYOUT 2026-10-23] Phones: squeezed to its 720 px floor the 1520-unit drawing (about 31 months) put its labels at
     4.5-6 px, and at that width there is no room for readable month labels; there it is drawn 1,680 px wide (it swipes
     sideways in ChartScroll), so a month label (10 units) is about 11 px. */
  const isMobile = useIsMobile();
  const n = months.length;
  if (!n) return <Empty>Nothing to draw yet.</Empty>;

  const W = 1520, H = 380, ml = 70, mr = line ? 64 : 20, mt = 28, mb = 46;
  const pw = W - ml - mr, ph = H - mt - mb;
  const totals = bars.map((b, i) => b + (bars2?.[i] ?? 0));
  const maxBar = Math.max(...totals, 1);
  const niceMax = (() => { const p = Math.pow(10, Math.floor(Math.log10(maxBar))); const s = maxBar / p; const m = s <= 1 ? 1 : s <= 2 ? 2 : s <= 2.5 ? 2.5 : s <= 5 ? 5 : 10; return m * p; })();
  const band = pw / n;
  const barW = Math.max(6, Math.min(band * 0.62, 44));
  const cx = (i: number) => ml + band * i + band / 2;
  const yBar = (v: number) => mt + ph - (v / niceMax) * ph;
  const yLine = (v: number) => mt + ph - (Math.max(0, Math.min(v, 120)) / 120) * ph;
  const ink = theme.text as string;
  const muted = theme.muted as string;
  const grid = theme.line as string;

  // Line segments only between consecutive months that both have a value (gaps break it).
  const segs: string[] = [];
  if (line) {
    let cur: string[] = [];
    line.forEach((v, i) => {
      if (v == null || gaps[i]) { if (cur.length > 1) segs.push(cur.join(' ')); cur = []; return; }
      cur.push(`${cur.length ? 'L' : 'M'} ${cx(i).toFixed(1)} ${yLine(v).toFixed(1)}`);
    });
    if (cur.length > 1) segs.push(cur.join(' '));
  }
  const rows = hover != null ? tip(hover) : [];
  const tipW = Math.max(150, Math.ceil(Math.max(...rows.map((r) => (r.label.length + 2 + r.value.length) * 4.6), fmtMonth(months[hover ?? 0]).length * 5.2)) + 20);
  const tipH = 26 + rows.length * 14;
  const tipX = hover != null ? Math.max(4, Math.min(W - tipW - 4, cx(hover) - tipW / 2)) : 0;
  const tipY = mt + 6;

  return (
    <ChartScroll>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', minWidth: isMobile ? 1680 : 720 /* [PHONE-LAYOUT 2026-10-23] */ }} onMouseLeave={() => setHover(null)} role="img" aria-label={barLabel}>
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line x1={ml} y1={mt + ph * (1 - f)} x2={W - mr} y2={mt + ph * (1 - f)} stroke={grid} strokeWidth={f === 0 ? 1.4 : 1} strokeDasharray={f === 0 ? undefined : '4 6'} />
            <text x={ml - 10} y={mt + ph * (1 - f) + 5} textAnchor="end" fontSize={13} fill={muted}>{fmtMoney(niceMax * f)}</text>
            {line && <text x={W - mr + 10} y={mt + ph * (1 - f) + 5} textAnchor="start" fontSize={13} fill={muted}>{Math.round(120 * f)}%</text>}
          </g>
        ))}
        {months.map((m, i) => (
          <g key={m}>
            {!gaps[i] && (
              <>
                <rect x={cx(i) - barW / 2} y={yBar(bars[i])} width={barW} height={Math.max(0, mt + ph - yBar(bars[i]))} rx={3} fill={barColor} opacity={hover == null || hover === i ? 1 : 0.55} />
                {bars2 && bars2[i] > 0 && (
                  <rect x={cx(i) - barW / 2} y={yBar(bars[i] + bars2[i])} width={barW} height={Math.max(0, yBar(bars[i]) - yBar(bars[i] + bars2[i]))} rx={3} fill={bar2Color ?? BLUE} opacity={hover == null || hover === i ? 1 : 0.55} />
                )}
              </>
            )}
            {gaps[i] && <text x={cx(i)} y={mt + ph - 8} textAnchor="middle" fontSize={11} fill={muted} opacity={0.8}>No clients</text>}
            <text x={cx(i)} y={H - 16} textAnchor="middle" fontSize={10} fill={muted}>{fmtMonthTick(m)}</text>
            <rect x={ml + band * i} y={mt} width={band} height={ph} fill="transparent" onMouseEnter={() => setHover(i)} />
          </g>
        ))}
        {line && segs.map((d, k) => <path key={k} d={d} fill="none" stroke={lineColor ?? theme.greenInk} strokeWidth={3.2} strokeLinejoin="round" strokeLinecap="round" />)}
        {line && line.map((v, i) => (v == null || gaps[i] ? null : (
          <circle key={months[i]} cx={cx(i)} cy={yLine(v)} r={hover === i ? 6.5 : 4.5} fill={lineColor ?? theme.greenInk} stroke={theme.surface} strokeWidth={2} />
        )))}
        {hover != null && (
          <g pointerEvents="none">
            <line x1={cx(hover)} y1={mt} x2={cx(hover)} y2={mt + ph} stroke={ink} strokeOpacity={0.25} strokeWidth={1.2} />
            {/* [ONE GLASS RECIPE] The same tooltip surface as Process Analysis and every
                other chart in the app — never an inline rect. */}
            <g transform={`translate(${tipX}, ${tipY})`}>
              <TipGlass w={tipW} h={tipH} dark={chartInk.dark} />
              <text x={10} y={16} fontSize="9.5" fontWeight={750} fill={chartInk.strong}>{fmtMonth(months[hover])}</text>
              {rows.map((r, k) => (
                <text key={r.label} x={10} y={31 + k * 14} fontSize="9" fill={chartInk.tipLabel}>
                  {r.label}: <tspan fill={chartInk.strong} fontWeight={700}>{r.value}</tspan>
                </text>
              ))}
            </g>
          </g>
        )}
      </svg>
      <Legend>
        <span><i style={{ background: barColor }} /> {barLabel}</span>
        {bars2 && bar2Label && <span><i style={{ background: bar2Color ?? BLUE }} /> {bar2Label}</span>}
        {line && lineLabel && <span><i className="line" style={{ background: lineColor ?? theme.greenInk }} /> {lineLabel} (right axis)</span>}
      </Legend>
    </ChartScroll>
  );
};



/**
 * [MY-PERF 2026-09-02] The chart ink, mirroring `useInk` in ProcessAnalysisPage.
 * ⚠ These values ARE the app's chart palette — keep them in step with that file. `axis` is for
 * LINE STROKES ONLY (never a text fill); `label`/`strong` are full ink, because the user has asked
 * repeatedly for no dim text on report pages.
 */
function useInk() {
  const theme = useTheme() as any;
  const dark = theme.colorScheme === 'dark';
  return {
    dark,
    axis: dark ? 'rgba(255,255,255,0.55)' : 'rgba(22,25,23,0.5)',
    label: dark ? '#ffffff' : '#161917',
    strong: dark ? '#ffffff' : '#161917',
    tipLabel: dark ? '#ffffff' : '#161917',
    grid: dark ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.08)',
    /** What was EXPECTED — the outer bar. Neutral on purpose: it is the thing being measured
     *  against, not a result, so it must never compete with the green inside it. */
    expected: dark ? 'rgba(255,255,255,0.30)' : 'rgba(22,25,23,0.20)',
    /** What ARRIVED — the inner bar. ⚠ MUST be the theme's own green: #1a7f64 is the LIGHT green
     *  and rendered muddy on the dark card; dark uses the app's mint, the same value theme.greenInk
     *  carries there. Do not put a single hard-coded green back here. */
    retained: dark ? '#4acd8b' : '#1e785f',
    /** Money that went back OUT, below the axis. Deep #c0392b disappears on dark, so dark takes
     *  the brighter red; light keeps the app's red at a readable weight. */
    outflow: dark ? '#ef4444' : '#c0392b',
    outflowLost: dark ? '#f59e0b' : '#c98a1b',
    /** Green for TEXT and dots — the token, never a literal (see the green-on-dark rule). */
    green: theme.greenInk as string,
    /** Solid hues for FIGURES (a translucent bar fill is unreadable as 9px text). */
    outflowText: dark ? '#f87171' : '#c0392b',
    outflowLostText: dark ? '#fbbf24' : '#c98a1b',
    /** The two overlaid lines, brightened for dark the same way. */
    upsell: dark ? '#60a5fa' : '#2563eb',
    won: dark ? '#a78bfa' : '#7c3aed',
  };
}

/**
 * ⚠ MONOTONE cubic (Fritsch–Carlson), copied deliberately from ProcessAnalysisPage so both pages
 * curve identically. Plain Catmull–Rom OVERSHOOTS: it can draw a peak above the highest real point,
 * which on a chart people read figures off is a lie rather than a style. Monotone interpolation
 * cannot leave the range of the two points it connects.
 */
function smoothPath(pts: { x: number; y: number }[]): string {
  const n = pts.length;
  if (n === 0) return '';
  if (n < 3) return pts.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const dx: number[] = [], dy: number[] = [], slope: number[] = [];
  for (let i = 0; i < n - 1; i += 1) {
    dx[i] = pts[i + 1].x - pts[i].x;
    dy[i] = pts[i + 1].y - pts[i].y;
    slope[i] = dx[i] === 0 ? 0 : dy[i] / dx[i];
  }
  const m: number[] = new Array(n);
  m[0] = slope[0];
  m[n - 1] = slope[n - 2];
  for (let i = 1; i < n - 1; i += 1) m[i] = (slope[i - 1] * slope[i] <= 0) ? 0 : (slope[i - 1] + slope[i]) / 2;
  for (let i = 0; i < n - 1; i += 1) {
    if (slope[i] === 0) { m[i] = 0; m[i + 1] = 0; continue; }
    const a = m[i] / slope[i], b = m[i + 1] / slope[i], s = a * a + b * b;
    if (s > 9) { const t = 3 / Math.sqrt(s); m[i] = t * a * slope[i]; m[i + 1] = t * b * slope[i]; }
  }
  let d = `M${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < n - 1; i += 1) {
    const h = dx[i] / 3;
    d += ` C${(pts[i].x + h).toFixed(1)},${(pts[i].y + m[i] * h).toFixed(1)}`
      + ` ${(pts[i + 1].x - h).toFixed(1)},${(pts[i + 1].y - m[i + 1] * h).toFixed(1)}`
      + ` ${pts[i + 1].x.toFixed(1)},${pts[i + 1].y.toFixed(1)}`;
  }
  return d;
}

/**
 * [MY-PERF 2026-09-02, user] THE JOURNEY CHART — the month-by-month money story.
 *
 * THE LAYOUT IS THE USER'S, and it is also the honest one:
 *   • Two bars SIDE BY SIDE per month — what was expected (pale) and what arrived (green) — on ONE
 *     shared money scale, so their heights are directly comparable.
 *   • The retention % is printed as a NUMBER above each pair, not drawn as a line. The first
 *     version put % on a second right-hand axis; on real data that line floated far above the
 *     bars and read as "we collected three times what was expected". A percentage and a dollar
 *     amount cannot share a plot honestly. As a number it is exact and it cannot mislead.
 *   • Refunds and lost disputes hang BELOW the zero line — money out, same scale, so their size
 *     is directly comparable to the money that came in.
 *   • Upsell and disputes won ride OVER the bars as lines. Both are dollars, so they belong on
 *     this same axis; a line separates "extra money" from the retention story underneath without
 *     needing a second scale. The disputes-won line is drawn only when there is any to show.
 *
 * IT FOLLOWS THE APP'S CHART LANGUAGE so the tab does not look imported: no y-axis labels (values
 * live on the bars and in the tooltip), three faint guides, rounded bars that lift on hover, the
 * monotone curve, and the shared TipGlass tooltip surface — the same recipe as Process Analysis.
 *
 * A month with no clients draws no bars and BREAKS the lines rather than plotting a false zero.
 */
const JourneyChart: React.FC<{ months: MyPerfMonth[] }> = ({ months }) => {
  const ink = useInk();
  const [hover, setHover] = useState<number | null>(null);
  /* [PHONE-LAYOUT 2026-10-23] Phones: squeezed to its 760 px floor its labels were 4.7-6.2 px; there it is drawn 1,860 px
     wide (it swipes sideways in ChartScroll), so a value label (9 units) is about 11 px. */
  const isMobile = useIsMobile();
  const n = months.length;
  if (!n) return <Empty>Nothing to draw yet.</Empty>;

  const upOf = (m: MyPerfMonth) => m.upsellRecurringUsd + m.upsellOneTimeUsd;
  const outOf = (m: MyPerfMonth) => m.refundsUsd + m.disputesLostUsd;
  const anyOut = months.some((m) => outOf(m) > 0);
  const anyWon = months.some((m) => m.disputesWonUsd > 0);

  const W = 1520, ml = 16, mr = 16, mt = 34, mb = 34;
  const OUT_H = anyOut ? 74 : 0;      // the strip below the zero line
  const IN_H = 268;                    // the money-in area
  const H = mt + IN_H + OUT_H + mb;
  const pw = W - ml - mr;

  const maxIn = Math.max(...months.map((m) => Math.max(m.placedUsd, m.retainedUsd, upOf(m), m.disputesWonUsd)), 1);
  const maxOut = Math.max(...months.map(outOf), 1);
  const zeroY = mt + IN_H;
  const yIn = (v: number) => zeroY - (v / maxIn) * IN_H;
  const yOut = (v: number) => zeroY + (v / maxOut) * OUT_H;

  const band = pw / n;
  /* One column per month: the OUTER bar is the inventory (expected) and the INNER bar sits inside
     it (arrived), so retention reads as how full the column looks. Both are centred on the band. */
  const barW = Math.max(10, Math.min(band * 0.66, 54));
  const innerW = Math.max(5, barW * 0.52);
  const cx = (i: number) => ml + band * i + band / 2;

  /* Lines are drawn in runs so a month the PM was not on the book breaks them instead of
     dragging the curve through a zero that never happened. */
  const runs = (value: (m: MyPerfMonth) => number) => {
    const out: { x: number; y: number }[][] = [];
    let cur: { x: number; y: number }[] = [];
    months.forEach((m, i) => {
      if (!m.onBook && value(m) === 0) { if (cur.length) out.push(cur); cur = []; return; }
      cur.push({ x: cx(i), y: yIn(value(m)) });
    });
    if (cur.length) out.push(cur);
    return out;
  };
  const upRuns = runs(upOf);
  const wonRuns = anyWon ? runs((m) => m.disputesWonUsd) : [];

  const hp = hover != null ? months[hover] : null;
  const tipRows: { label: string; value: string; color: string; weight: number }[] = hp ? [
    { label: 'Clients', value: hp.onBook ? String(hp.clients) : 'none', color: ink.strong, weight: 700 },
    { label: 'Inventory', value: fmtMoney(hp.placedUsd), color: ink.strong, weight: 700 },
    { label: 'Retained', value: fmtMoney(hp.retainedUsd), color: ink.green, weight: 750 },
    { label: 'Retention', value: fmtPct(hp.retentionPct), color: ink.green, weight: 750 },
    ...(upOf(hp) > 0 ? [{ label: 'Upsell', value: fmtMoney(upOf(hp)), color: ink.upsell, weight: 750 }] : []),
    ...(hp.refundsUsd > 0 ? [{ label: 'Refunds', value: `-${fmtMoney(hp.refundsUsd)}`, color: ink.outflowText, weight: 700 }] : []),
    ...(hp.disputesLostUsd > 0 ? [{ label: 'Lost disputes', value: `-${fmtMoney(hp.disputesLostUsd)}`, color: ink.outflowLostText, weight: 700 }] : []),
    ...(hp.disputesWonUsd > 0 ? [{ label: 'Disputes won', value: `+${fmtMoney(hp.disputesWonUsd)}`, color: ink.won, weight: 700 }] : []),
  ] : [];
  /* Width from the character count — SVG text cannot be measured without touching the DOM, and a
     fixed width silently clipped the longest row. Same estimate Process Analysis uses. */
  const tipW = Math.max(190, Math.ceil(Math.max(
    ...tipRows.map((r) => (r.label.length + 2 + r.value.length) * 4.6),
    hp ? fmtMonth(hp.month).length * 5.2 : 0,
  )) + 22);
  const tipH = 26 + tipRows.length * 14;
  const tipX = hover != null ? Math.max(4, Math.min(W - tipW - 4, cx(hover) - tipW / 2)) : 0;
  const anchor = hp ? Math.min(yIn(Math.max(hp.placedUsd, hp.retainedUsd, upOf(hp))), zeroY) : 0;
  const tipY = anchor - tipH - 12 < 4 ? anchor + 16 : anchor - tipH - 12;

  return (
    <ChartScroll>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', minWidth: isMobile ? 1860 : 760 /* [PHONE-LAYOUT 2026-10-23] */ }} onMouseLeave={() => setHover(null)}
        role="img" aria-label="Inventory and retained money for each month, with upsell, disputes won and money paid back out">

        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={ml} y1={mt + IN_H * f} x2={W - mr} y2={mt + IN_H * f} stroke={ink.grid} strokeWidth={1} />
        ))}
        {anyOut && <line x1={ml} y1={zeroY + OUT_H} x2={W - mr} y2={zeroY + OUT_H} stroke={ink.grid} strokeWidth={1} />}
        {/* The zero line carries the meaning when money goes both ways, so it is drawn at full weight. */}
        <line x1={ml} y1={zeroY} x2={W - mr} y2={zeroY} stroke={ink.axis} strokeWidth={anyOut ? 1 : 0.75} />

        {hover != null && (
          <line x1={cx(hover)} y1={mt} x2={cx(hover)} y2={zeroY + OUT_H} stroke={ink.axis} strokeWidth={0.75} strokeDasharray="3 3" opacity={0.5} />
        )}

        {months.map((m, i) => {
          const lift = {
            transition: 'opacity 160ms cubic-bezier(0.4, 0, 0.2, 1), transform 160ms cubic-bezier(0.4, 0, 0.2, 1), filter 160ms cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: hover == null || hover === i ? 1 : 0.72,
            transform: hover === i ? 'translateY(-4px)' : 'translateY(0px)',
            filter: hover === i ? 'brightness(1.08)' : 'none',
          } as React.CSSProperties;
          return (
            <g key={m.month}>
              {m.onBook ? (
                <>
                  <rect x={cx(i) - barW / 2} y={yIn(m.placedUsd)} width={barW} height={Math.max(0, zeroY - yIn(m.placedUsd))} rx={6} fill={ink.expected} style={lift} />
                  <rect x={cx(i) - innerW / 2} y={yIn(m.retainedUsd)} width={innerW} height={Math.max(0, zeroY - yIn(m.retainedUsd))} rx={4} fill={ink.retained} style={lift} />
                  {/* The retention % above the pair — the number the two bars exist to explain. */}
                  {m.retentionPct != null && (
                    <text x={cx(i)} y={yIn(Math.max(m.placedUsd, m.retainedUsd)) - 7} fontSize="9" textAnchor="middle" fontWeight={650}
                      fill={m.retentionPct >= 90 ? ink.green : m.retentionPct < 80 ? ink.outflowText : ink.label}
                      style={{ transition: 'opacity 120ms cubic-bezier(0.4, 0, 0.2, 1)', opacity: hover == null || hover === i ? 1 : 0.45 }}>
                      {Math.round(m.retentionPct)}%
                    </text>
                  )}
                </>
              ) : (
                <text x={cx(i)} y={zeroY - 8} fontSize="9" textAnchor="middle" fill={ink.label} opacity={0.55}>none</text>
              )}
              {m.refundsUsd > 0 && <rect x={cx(i) - innerW / 2} y={zeroY} width={innerW} height={Math.max(0, yOut(m.refundsUsd) - zeroY)} rx={4} fill={ink.outflow} style={lift} />}
              {m.disputesLostUsd > 0 && (
                <rect x={cx(i) - innerW / 2} y={yOut(m.refundsUsd)} width={innerW}
                  height={Math.max(0, yOut(m.refundsUsd + m.disputesLostUsd) - yOut(m.refundsUsd))} rx={4} fill={ink.outflowLost} style={lift} />
              )}
              <rect x={ml + band * i} y={mt} width={band} height={IN_H + OUT_H} fill="transparent" onMouseEnter={() => setHover(i)} />
            </g>
          );
        })}

        {upRuns.map((pts, k) => <path key={`u${k}`} d={smoothPath(pts)} fill="none" stroke={ink.upsell} strokeWidth={2.4} strokeLinejoin="round" strokeLinecap="round" />)}
        {months.map((m, i) => (m.onBook || upOf(m) > 0 ? (
          <circle key={`ud${m.month}`} cx={cx(i)} cy={yIn(upOf(m))} r={hover === i ? 4.5 : 2.8} fill={ink.upsell} style={{ transition: 'r 120ms cubic-bezier(0.4, 0, 0.2, 1)' }} />
        ) : null))}
        {wonRuns.map((pts, k) => <path key={`w${k}`} d={smoothPath(pts)} fill="none" stroke={ink.won} strokeWidth={2} strokeDasharray="5 4" strokeLinejoin="round" strokeLinecap="round" />)}
        {/* A dispute win is a RARE event — Sana Alam has three in twenty-nine months. As a line
            alone it lies flat on zero and the wins are easy to miss, so each month that actually
            has one gets a marker. The line still carries the shape the user asked for. */}
        {months.map((m, i) => (m.disputesWonUsd > 0 ? (
          <circle key={`wd${m.month}`} cx={cx(i)} cy={yIn(m.disputesWonUsd)} r={hover === i ? 5 : 3.4}
            fill={ink.won} stroke={ink.dark ? "#181818" : "#ffffff"} strokeWidth={1.4}
            style={{ transition: "r 120ms cubic-bezier(0.4, 0, 0.2, 1)" }} />
        ) : null))}

        {months.map((m, i) => (
          <text key={`x${m.month}`} x={cx(i)} y={H - 11} fontSize="10" fill={ink.strong} textAnchor="middle"
            fontWeight={hover === i ? 750 : 500}>
            {fmtMonthTick(m.month)}
          </text>
        ))}

        <g transform={`translate(${tipX}, ${tipY})`} pointerEvents="none"
          style={{ transition: 'transform 120ms cubic-bezier(0.4, 0, 0.2, 1), opacity 120ms cubic-bezier(0.4, 0, 0.2, 1)', opacity: hp ? 1 : 0 }}>
          <TipGlass w={tipW} h={tipH} dark={ink.dark} />
          {hp && (
            <>
              <text x={10} y={16} fontSize="9.5" fontWeight={750} fill={ink.strong}>{fmtMonth(hp.month)}</text>
              {tipRows.map((r, k) => (
                <text key={r.label} x={10} y={31 + k * 14} fontSize="9" fill={ink.tipLabel}>
                  {r.label}: <tspan fill={r.color} fontWeight={r.weight}>{r.value}</tspan>
                </text>
              ))}
            </>
          )}
        </g>
      </svg>
      <Legend>
        <span><i style={{ background: ink.expected }} /> Inventory</span>
        <span><i style={{ background: ink.retained }} /> Retained (the inner bar) · % shown above each column</span>
        <span><i className="line" style={{ background: ink.upsell }} /> Upsell</span>
        {anyWon && <span><i className="line" style={{ background: ink.won }} /> Disputes won</span>}
        {anyOut && <span><i style={{ background: ink.outflow }} /> Refunds (below the line)</span>}
        {anyOut && <span><i style={{ background: ink.outflowLost }} /> Lost disputes</span>}
      </Legend>
    </ChartScroll>
  );
};

/**
 * [SKELETON ALIGNMENT 2026-09-03, user] The loading state, built from THIS page's own <Section>,
 * <Grid> and <Tile> — not from the shared <KpiSkeleton>.
 *
 * The shared one is laid out differently in six ways: it wraps everything in a padded, bordered box
 * the real content does not have; its grid is `auto-fill, minmax(150px)` with a 12px gap against
 * this page's `auto-fit, minmax(172px)` with 10px; its single 160px title bar stands in for an
 * eyebrow plus a title, rule and subtitle; and its flat 300px block matches nothing in Section 03.
 * The result was tiles visibly jumping sideways and upwards the moment data arrived.
 *
 * ⚠ Do NOT "fix" this by editing components/Skeleton.tsx — InventoryPage, InventoryViewPage and
 * MonthOnMonthPage all render KpiSkeleton and would shift with it. Reusing the real layout
 * components here means this skeleton cannot drift out of alignment again: if the grid or the tile
 * padding changes, both states change together.
 */
const LoadingShape: React.FC = () => (
  <>
    <Section aria-busy="true" aria-label="Loading">
      <SkeletonBox $w="74px" $h="11px" />
      <SectionHead style={{ marginTop: 3 }}>
        <SkeletonBox $w="190px" $h="15px" />
      </SectionHead>
      <Grid $min={172}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Tile key={i}>
            <SkeletonBox $w="62%" $h="10px" />
            <SkeletonBox $w="70%" $h="22px" $mt="8px" />
            <SkeletonBox $w="86%" $h="11px" $mt="9px" />
          </Tile>
        ))}
      </Grid>
      <div style={{ height: 12 }} />
      <SkeletonBox $w="260px" $h="11px" />
      <div style={{ height: 10 }} />
      <Grid $min={172}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Tile key={i}>
            <SkeletonBox $w="62%" $h="10px" />
            <SkeletonBox $w="70%" $h="22px" $mt="8px" />
            <SkeletonBox $w="86%" $h="11px" $mt="9px" />
          </Tile>
        ))}
      </Grid>
    </Section>
    <Section aria-hidden="true">
      <SkeletonBox $w="74px" $h="11px" />
      <SectionHead style={{ marginTop: 3 }}>
        <SkeletonBox $w="230px" $h="15px" />
      </SectionHead>
      <Card>
        <SkeletonBox $h="300px" style={{ borderRadius: 10 }} />
      </Card>
    </Section>
  </>
);

/* ── page ──────────────────────────────────────────────────────────────────────────────── */
/**
 * The chart in one sentence. Kept deliberately plain — it names what arrived against what was
 * expected, what upsell added, what went back out, and the two months worth remembering. Every
 * number here is one the chart above already draws, so the two can never disagree.
 */
function journeySummary(pm: string, s: NonNullable<MyPerfData['summary']>): React.ReactNode {
  const L = s.lifetime;
  const upsell = L.upsellRecurringUsd + L.upsellOneTimeUsd;
  const out = L.refundsUsd + L.disputesLostUsd;
  return (
    <>
      Across <b>{plural(s.monthsOnBook, 'month')}</b> with clients, {pm} was expected to bring in{' '}
      <b>{fmtMoney(L.placedUsd)}</b> and <b>{fmtMoney(L.retainedUsd)}</b> arrived
      {L.retentionPct != null && <> — <b>{fmtPct(L.retentionPct)}</b></>}
      {upsell > 0 && <>, plus <b>{fmtMoney(upsell)}</b> of upsell</>}
      {out > 0 && <>, with <b>{fmtMoney(out)}</b> paid back out</>}
      .
      {s.bestMonth && s.weakestMonth && s.bestMonth.month !== s.weakestMonth.month && (
        <> Best month <b>{fmtMonth(s.bestMonth.month)}</b> at {fmtPct(s.bestMonth.retentionPct)}, weakest{' '}
          <b>{fmtMonth(s.weakestMonth.month)}</b> at {fmtPct(s.weakestMonth.retentionPct)}.</>
      )}
      {s.biggestBook && <> Biggest book <b>{fmtMonth(s.biggestBook.month)}</b> at {fmtMoney(s.biggestBook.placedUsd)}.</>}
    </>
  );
}

/**
 * Where this PM stands in the last complete month, in plain English, against the COMPANY's own
 * retention for that same month. Everything is computed from the month beside it — including the
 * "worth closing" figure, which is the gap to the company line priced on this PM's actual book
 * (points of a percentage are meaningless to a reader; dollars are not).
 *
 * ⚠ Nothing here is asserted. If the company figure is missing the comparison is dropped rather
 * than guessed, and a month where more went out than came in is described as exactly that.
 */
/**
 * A tile's change against the same month a year earlier. Percentages move in POINTS (68.99% against
 * 40.19% is +28.8 points, never "+72%"); money moves in per cent. `invert` is for figures where up
 * is bad — money paid back out — so the colour follows the meaning rather than the arithmetic.
 * Returns null when there is no year-ago month, and the caller renders nothing at all.
 */
function deltaChip(now: number | null | undefined, then: number | null | undefined,
  kind: 'money' | 'points', invert = false): React.ReactNode {
  if (now == null || then == null) return null;
  const diff = now - then;
  if (Math.abs(diff) < (kind === 'points' ? 0.05 : 0.5)) return <Delta $tone="flat">no change</Delta>;
  const up = diff > 0;
  const good = invert ? !up : up;
  const text = kind === 'points'
    ? `${up ? '+' : ''}${diff.toFixed(1)} pts`
    : then === 0 ? 'new' : `${up ? '+' : ''}${Math.round((diff / Math.abs(then)) * 100)}%`;
  return <Delta $tone={good ? 'good' : 'bad'}>{up ? '▲' : '▼'} {text}</Delta>;
}

/**
 * The closing verdict. ⚠ EVERY bullet is a fixed threshold applied to a number shown elsewhere on
 * this page — no judgement, nothing the page cannot evidence, and a test with no data behind it is
 * simply omitted rather than guessed. Keep it that way: the value of this block is that a PM can
 * trace any line back to a figure above it.
 */
function verdict(pm: string, months: MyPerfMonth[], s: NonNullable<MyPerfData['summary']>):
{ good: React.ReactNode[]; bad: React.ReactNode[] } {
  const L = s.lifetime;
  const on = months.filter((m) => m.onBook);
  const good: React.ReactNode[] = [];
  const bad: React.ReactNode[] = [];

  // 1. Did they hold on to client money better than the other PMs?
  const cmp = on.filter((m) => m.retentionPct != null && m.avgRetentionPct != null);
  const above = cmp.filter((m) => (m.retentionPct as number) >= (m.avgRetentionPct as number)).length;
  if (cmp.length >= 6) {
    if (above / cmp.length >= 0.6) {
      good.push(<>Held on to more of the client money than the other PMs did in <b>{above} of {cmp.length} months</b> — better than the rest of the company most of the time.</>);
    } else {
      bad.push(<>Held on to less of the client money than the other PMs did in <b>{cmp.length - above} of {cmp.length} months</b> — behind the rest of the company more often than not.</>);
    }
  }

  // 2. How hard is each managed dollar made to work, next to everyone else?
  const myPlaced = on.reduce((a, m) => a + m.placedUsd, 0);
  const myUp = on.reduce((a, m) => a + m.upsellRecurringUsd + m.upsellOneTimeUsd, 0);
  const restPlaced = on.reduce((a, m) => a + m.restPlacedUsd, 0);
  const restUp = on.reduce((a, m) => a + m.restUpsellUsd, 0);
  if (myPlaced > 0 && restPlaced > 0 && restUp > 0) {
    const r = (myUp / myPlaced) / (restUp / restPlaced);
    if (r >= 1.15) good.push(<>Sells <b>{r.toFixed(1)} times more</b> extra business than the other PMs do, for every $1 of client money managed.</>);
    else if (r <= 0.85) bad.push(<>Sells <b>{Math.round((1 - r) * 100)}% less</b> extra business than the other PMs do, for every $1 of client money managed.</>);
  }

  // 3. Of every dollar the clients were meant to pay, how much actually ended up in the bank?
  if (L.bankedPer100 != null) {
    const c = Math.round(L.bankedPer100);
    if (L.bankedPer100 >= 100) {
      good.push(<>For every <b>$1</b> the clients were expected to pay, <b>{c}c</b> ended up in the bank — the extra business sold more than covered whatever did not come in.</>);
    } else {
      bad.push(<>For every <b>$1</b> the clients were expected to pay, only <b>{c}c</b> ended up in the bank — what went unpaid and refunded was not fully made up by extra sales.</>);
    }
  }

  // 4. Months where more money left than arrived.
  const red = months.filter((m) => m.currentNetInBankUsd < 0);
  if (red.length > 0) {
    bad.push(<>In <b>{plural(red.length, 'month')}</b> more money went back out in refunds than came in at all — {red.map((m) => fmtMonth(m.month)).join(', ')}.</>);
  } else if (on.length >= 6) {
    good.push(<>No month has ever ended with more money going back out than came in.</>);
  }

  // 5. Does the extra business raise the monthly book, or is it one-off cash?
  const total = L.upsellRecurringUsd + L.upsellOneTimeUsd;
  if (total > 0) {
    const oneTime = Math.round((L.upsellOneTimeUsd / total) * 100);
    if (oneTime >= 80) {
      bad.push(<><b>{oneTime}%</b> of the extra business sold is a one-off payment, so it does not raise what the clients pay every month.</>);
    } else if (oneTime <= 60) {
      good.push(<><b>{100 - oneTime}%</b> of the extra business sold repeats every month, which permanently raises what these clients pay.</>);
    }
    // 6. Is the total built on a few deals or many?
    if (L.upsellTop5Pct != null && L.upsellTop5Pct >= 40) {
      bad.push(<>The <b>5 biggest deals</b> make up <b>{Math.round(L.upsellTop5Pct)}%</b> of all the extra business sold — losing one of them would take a large bite out of the total.</>);
    } else if (L.upsellTop5Pct != null && L.upsellDistinctClients >= 10) {
      good.push(<>The extra business came from <b>{plural(L.upsellDistinctClients, 'different client')}</b>, so the total is not resting on one or two lucky deals.</>);
    }
  }
  return { good, bad };
}

function standingNarrative(pm: string, m: MyPerfMonth): React.ReactNode {
  const diff = m.retentionPct != null && m.avgRetentionPct != null ? m.retentionPct - m.avgRetentionPct : null;
  // What the gap to the company line is worth on THIS book, in money.
  const worth = diff != null && diff < 0 ? (Math.abs(diff) / 100) * m.placedUsd : null;

  return (
    <>
      <h4>What this means</h4>
      {/* [LIGHT-SECTION-1 2026-09-09, user] ONE LINE, and it explains the TILES above it — nothing
          else. The upsell and money-back-out figures that used to be read out here are the Upsell
          and Money back out tiles directly above, so the reader met every number twice; that, not
          sentence length, is what made the section feel heavy. Only what no tile carries is left:
          the company comparison and what the month actually kept.
          ⛔ Do not re-add a figure that already has a tile, and ⛔ keep it to a single sentence. */}
      <p>
        {diff != null && (
          <>
            In <b>{fmtMonth(m.month)}</b> the company kept <b>{fmtPct(m.avgRetentionPct)}</b> of its client money and{' '}
            {pm} kept <b>{fmtPct(m.retentionPct)}</b>
            {Math.abs(diff) < 0.5
              ? <> — the two are level</>
              : diff > 0
                ? <> — more than the rest of the company managed</>
                : <> — matching it was worth about <b>{fmtMoney(worth as number)}</b> more</>}
            {', '}
          </>
        )}
        {m.currentNetInBankUsd < 0
          ? <>{diff != null ? 'and the' : 'The'} month ended at <b>{fmtMoney(m.currentNetInBankUsd)}</b>, with more going out than came in.</>
          : <>{diff != null ? 'leaving' : 'The month left'} <b>{fmtMoney(m.currentNetInBankUsd)}</b> kept{m.bankedPer100 != null && <>, {perDollar(m.bankedPer100)} for every $1 looked after</>}.</>}
      </p>

    </>
  );
}

/**
 * The same reading, for the whole period. The figure worth having here is not another total — the
 * tiles already carry those — it is HOW OFTEN this PM beat the company line, which no single
 * percentage can say. A PM at 86% overall who was above the line in 24 of 29 months is a steady
 * performer; one who got there on three enormous months is not, and only the count separates them.
 */
function lifetimeNarrative(pm: string, s: NonNullable<MyPerfData['summary']>, months: MyPerfMonth[]): React.ReactNode {
  const L = s.lifetime;
  const out = L.refundsUsd + L.disputesLostUsd;
  const compared = months.filter((m) => m.onBook && m.retentionPct != null && m.avgRetentionPct != null);
  const above = compared.filter((m) => (m.retentionPct as number) >= (m.avgRetentionPct as number)).length;

  return (
    <>
      <h4>Over the whole period</h4>
      {/* [LIGHT-SECTION-1 2026-09-09, user] ONE LINE, same rule as the monthly panel above. Upsell,
          cents-per-$1 and clients in/out were read out here and are all TILES in the grid directly
          above; repeating them is what made this feel like a wall. What survives is the one thing
          no tile can show — how OFTEN this PM matched the company — and the money with no tile.
          ⛔ No sentence for a number that already has a tile, and ⛔ keep it to a single sentence. */}
      <p>
        {compared.length > 0 && (
          <>
            {pm} matched or beat the rest of the company in <b>{above} of {compared.length} months</b>
            {above / compared.length >= 0.6
              ? <> — steady rather than a good run</>
              : <> — so the total leans on the months that went well</>}
            {', and '}
          </>
        )}
        {out > 0
          ? <>{compared.length > 0 ? 'after' : 'After'} <b>{fmtMoney(out)}</b> of refunds and lost disputes the company kept <b>{fmtMoney(L.currentNetInBankUsd)}</b> in total.</>
          : <>{compared.length > 0 ? 'the' : 'The'} company kept <b>{fmtMoney(L.currentNetInBankUsd)}</b> in total.</>}
      </p>

    </>
  );
}

/**
 * What the journey chart MEANS. Deliberately about the SHAPE over time — direction, steadiness, and
 * the average distance from the company line — because the flat totals are already covered: the
 * summary strip under the chart carries the period totals and best/weakest months, and Section 01's
 * lifetime panel counts the months spent above the line. Repeating either here would pad the page
 * without telling the reader anything new.
 *
 * The gap to the company is given as a MEAN of the monthly gaps, not a count. A book can be above
 * the line more often than not and still average below it (a few heavy losses outweigh many small
 * wins), and the two readings together say more than either alone.
 */
/**
 * This year against last, SAME MONTHS ONLY. Apr–Aug 2026 is compared with Apr–Aug 2025, never with
 * the whole of 2025 — otherwise a part-finished year always looks worse than a complete one, which
 * is an artefact of the calendar rather than anything the PM did.
 *
 * Returns null (and the section renders nothing) unless there are at least three comparable months
 * on BOTH sides: below that it is not a trend, and a PM in their first year would otherwise be
 * shown a page of dashes. A prior year of zero upsell reads "from nothing", never a % of zero.
 */
function yearOnYear(months: MyPerfMonth[]): {
  label: string; rows: { metric: string; prev: string; cur: string; delta: React.ReactNode }[];
  prevYear: string; curYear: string;
} | null {
  const on = months.filter((m) => m.onBook);
  if (!on.length) return null;
  const curYear = on[on.length - 1].month.slice(0, 4);
  const cur = on.filter((m) => m.month.slice(0, 4) === curYear);
  const prevYear = String(Number(curYear) - 1);
  const wanted = new Set(cur.map((m) => m.month.slice(5)));
  const prev = on.filter((m) => m.month.slice(0, 4) === prevYear && wanted.has(m.month.slice(5)));
  if (cur.length < 3 || prev.length < 3) return null;

  const agg = (a: MyPerfMonth[]) => {
    const placed = a.reduce((x, m) => x + m.placedUsd, 0);
    const retained = a.reduce((x, m) => x + m.retainedUsd, 0);
    return {
      placed,
      retained,
      pct: placed > 0 ? (100 * retained) / placed : null,
      upsell: a.reduce((x, m) => x + m.upsellRecurringUsd + m.upsellOneTimeUsd, 0),
      net: a.reduce((x, m) => x + m.currentNetInBankUsd, 0),
    };
  };
  const A = agg(prev); const B = agg(cur);
  // Only the months actually present on BOTH sides are named, so the label cannot overstate the span.
  const shared = cur.filter((m) => prev.some((q) => q.month.slice(5) === m.month.slice(5)));
  const label = shared.length
    ? `${fmtMonth(`2000-${shared[0].month.slice(5)}`).split(' ')[0]}–${fmtMonth(`2000-${shared[shared.length - 1].month.slice(5)}`).split(' ')[0]}`
    : '';
  return {
    label, prevYear, curYear,
    rows: [
      { metric: 'Inventory', prev: fmtMoney(A.placed), cur: fmtMoney(B.placed), delta: deltaChip(B.placed, A.placed, 'money') },
      { metric: 'Retained', prev: fmtMoney(A.retained), cur: fmtMoney(B.retained), delta: deltaChip(B.retained, A.retained, 'money') },
      { metric: 'Retention %', prev: fmtPct(A.pct), cur: fmtPct(B.pct), delta: deltaChip(B.pct, A.pct, 'points') },
      { metric: 'Upsell', prev: fmtMoney(A.upsell), cur: fmtMoney(B.upsell), delta: deltaChip(B.upsell, A.upsell, 'money') },
      { metric: 'Current Net In Bank', prev: fmtMoney(A.net), cur: fmtMoney(B.net), delta: deltaChip(B.net, A.net, 'money') },
    ],
  };
}

function journeyInsight(pm: string, months: MyPerfMonth[]): React.ReactNode {
  const on = months.filter((m) => m.onBook);
  if (on.length === 0) return <><h4>What this shows</h4><p>No clients to look after yet.</p></>;
  // A range, an average gap and a direction all need history. Below three months they are not
  // readings, they are one or two data points wearing the language of a trend.
  if (on.length < 3) {
    return (
      <>
        <h4>What this shows</h4>
        <p>Only <b>{plural(on.length, 'month')}</b> with clients so far, which is too early to see a pattern. The chart will start to mean something once there are three or more.</p>
      </>
    );
  }

  // Direction: the average book over the first and last stretches, not endpoints — a single big or
  // empty month at either end would otherwise decide the story.
  const win = Math.min(6, Math.max(1, Math.floor(on.length / 3)));
  const early = on.slice(0, win).reduce((a, m) => a + m.placedUsd, 0) / win;
  const late = on.slice(-win).reduce((a, m) => a + m.placedUsd, 0) / win;
  const growth = early > 0 ? ((late - early) / early) * 100 : null;

  const pcts = on.map((m) => m.retentionPct).filter((x): x is number => x != null);
  const full = pcts.filter((x) => x >= 99.5).length;
  const weak = pcts.filter((x) => x < 80).length;
  const lo = pcts.length ? Math.min(...pcts) : null;
  const hi = pcts.length ? Math.max(...pcts) : null;

  const cmp = on.filter((m) => m.retentionPct != null && m.avgRetentionPct != null);
  const meanGap = cmp.length
    ? cmp.reduce((a, m) => a + ((m.retentionPct as number) - (m.avgRetentionPct as number)), 0) / cmp.length
    : null;
  /* [PLAIN-ENGLISH 2026-09-09, user] The two sides of that gap, printed rather than the gap itself:
     "kept 84.9% against the company's 82.3%" needs no explanation, where "2.6 points above" asks the
     reader to hold a subtraction in their head. Both are means over the SAME months (`cmp`), so
     myMean - coMean is exactly meanGap — the sentence and the branch below can never disagree. */
  const myMean = cmp.length ? cmp.reduce((a, m) => a + (m.retentionPct as number), 0) / cmp.length : null;
  const coMean = cmp.length ? cmp.reduce((a, m) => a + (m.avgRetentionPct as number), 0) / cmp.length : null;

  const outTotal = months.reduce((a, m) => a + m.refundsUsd + m.disputesLostUsd, 0);
  const worst = months.reduce<MyPerfMonth | null>(
    (b, m) => (m.refundsUsd + m.disputesLostUsd > (b ? b.refundsUsd + b.disputesLostUsd : 0) ? m : b), null);
  const worstUsd = worst ? worst.refundsUsd + worst.disputesLostUsd : 0;

  return (
    <>
      <h4>What this shows</h4>
      {growth != null && Math.abs(growth) >= 1 && (
        <p>
          {pm} looks after <b>{growth > 0 ? 'more' : 'less'}</b> client money now than at the start: about{' '}
          <b>{fmtMoney(early)}</b> a month back then, <b>{fmtMoney(late)}</b> a month now
          {growth > 200
            ? <>. That is a total built up from a small start, not a sudden jump.</>
            : <>, which is <b>{growth > 0 ? 'up' : 'down'} {Math.abs(Math.round(growth))}%</b>.</>}{' '}
          {growth > 0
            ? 'The more money they look after, the more the company depends on them keeping it.'
            : 'The less money they look after, the less every other number on this page can add up to.'}
        </p>
      )}
      {lo != null && hi != null && (
        <p>
          The share of client money that stayed has been as low as <b>{fmtPct(lo)}</b> and as high as <b>{fmtPct(hi)}</b>
          {full > 0 && <>. Every dollar stayed in <b>{plural(full, 'month')}</b></>}
          {weak > 0 && <>{full > 0 ? ', and it' : '. It'} fell below <b>80%</b> in <b>{plural(weak, 'month')}</b></>}.{' '}
          {weak === 0
            ? 'Never dropping below 80% is the steady kind of month the company plans its cash around.'
            : 'The bad months cost more than the good months bring in.'}
        </p>
      )}
      {meanGap != null && (
        <p>
          Month for month, {pm} kept <b>{fmtPct(myMean)}</b> of the client money on average. The rest of the company
          kept <b>{fmtPct(coMean)}</b> over the same months.{' '}
          {Math.abs(meanGap) < 0.5
            ? <>The two are level.</>
            : meanGap > 0
              ? <>So this PM has been holding on to more of it than most.</>
              : <>So the company as a whole held on to more of its money than this PM did.</>}
        </p>
      )}
      {outTotal > 0 && worst && (
        <p>
          <b>{fmtMoney(outTotal)}</b> went back to clients over this time
          {worstUsd > 0 && <>, and <b>{Math.round((worstUsd / outTotal) * 100)}%</b> of that was in <b>{fmtMonth(worst.month)}</b> alone</>}.
          The bars below the line are money the company had earned and then gave back.
        </p>
      )}
      <p>
        <em>Why this chart matters: it shows how much money came in, how much of it stayed, and how much
        went back out. Keeping a steady level every month is worth more than a few great months mixed
        with bad ones.</em>
      </p>
    </>
  );
}

/**
 * The reading under the month table. Every other panel on this page is about RETENTION — this one
 * is deliberately about the MONEY THAT LANDED (Current Net In Bank), because that is the column the
 * table adds and the one the rest of the page cannot show: a month can retain well and still close
 * badly once a refund lands, and only this figure says so.
 *
 * ⚠ Best/worst here are by money banked, NOT by retention %. The summary strip under the journey
 * chart already names the best and weakest months by retention, and they are frequently different
 * months — that difference is the point, not a bug.
 */
function tableSummary(pm: string, months: MyPerfMonth[]): React.ReactNode {
  const on = months.filter((m) => m.onBook);
  const black = on.filter((m) => m.currentNetInBankUsd > 0).length;
  const red = months.filter((m) => m.currentNetInBankUsd < 0);
  const ranked = [...on].sort((a, b) => b.currentNetInBankUsd - a.currentNetInBankUsd);
  const best = ranked[0];
  const worst = ranked[ranked.length - 1];

  // Direction on MONEY: the recent stretch against the one before it.
  const win = Math.min(6, Math.floor(on.length / 2));
  const recent = win > 0 ? on.slice(-win).reduce((a, m) => a + m.currentNetInBankUsd, 0) / win : null;
  const prior = win > 0 ? on.slice(-win * 2, -win).reduce((a, m) => a + m.currentNetInBankUsd, 0) / win : null;

  return (
    <>
      <h4>What the table adds</h4>
      <p>
        {pm} had clients in <b>{plural(on.length, 'month')}</b>. In <b>{black}</b> of them the company ended up with money
        {red.length > 0
          ? <>. In <b>{plural(red.length, 'month')}</b> it <ToneText $tone="bad">lost money</ToneText> — {red.map((m) => fmtMonth(m.month)).join(', ')} — because more was refunded than came in.</>
          : <>, and it never ended a month out of pocket.</>}
        {best && worst && best.month !== worst.month && (
          <> The best month was <b>{fmtMonth(best.month)}</b> with <b>{fmtMoney(best.currentNetInBankUsd)}</b>. The worst was <b>{fmtMonth(worst.month)}</b> at <b>{fmtMoney(worst.currentNetInBankUsd)}</b>.</>
        )}
      </p>
      {recent != null && prior != null && win >= 3 && (
        <p>
          Over the last <b>{win} months</b> these clients left the company <b>{fmtMoney(recent)}</b> a month. In the{' '}
          <b>{win} months</b> before that it was <b>{fmtMoney(prior)}</b> a month.{' '}
          {Math.abs(recent - prior) < Math.max(1, prior * 0.05)
            ? <>That is holding steady.</>
            : recent > prior
              ? <>It is <ToneText $tone="good">going up</ToneText>, so the recent months are the better guide to what this PM brings in now.</>
              : <>It is <ToneText $tone="bad">going down</ToneText>, and the totals above will hide that for a while yet.</>}
        </p>
      )}
      <p>
        <em>Why this table matters: it shows what the company actually kept each month, after refunds and
        everything else went back out. That is the number the company can plan with, and the fairest way
        to compare one PM against another. For {pm} it is a month-by-month record: which months to
        repeat, and which to learn from.</em>
      </p>
    </>
  );
}

/**
 * What the upsell chart MEANS, measured against everyone else.
 *
 * ⚠ THE COMPARISON IS COMPUTED OVER THE SAME MONTHS THIS PANEL DESCRIBES, and the period is named
 * in the text. Mixing windows is how one PM ended up with two different yields on one page (30c
 * lifetime against 62c over a recent year), so the rate and the rate it is compared with are always
 * the same span. "Everyone else" excludes this PM, for the same reason the retention benchmark
 * does: a big seller must not be measured partly against themselves.
 *
 *   1. THE MIX — recurring lifts the monthly book for good; one-time is cash once.
 *   2. THE RATE — cents of upsell per $1 of inventory, which lets a small book be compared with a
 *      large one, set against what every other PM managed over the very same months.
 *   3. THE CONCENTRATION — whether the total rests on a few big deals or many. Two identical totals
 *      are not the same asset, and only this separates them.
 */
function upsellInsight(pm: string, months: MyPerfMonth[], s: NonNullable<MyPerfData['summary']>): React.ReactNode {
  const L = s.lifetime;
  const total = L.upsellRecurringUsd + L.upsellOneTimeUsd;
  if (total <= 0 || L.upsellDeals === 0) {
    return (
      <>
        <h4>What this shows</h4>
        <p>{pm} has no extra work recorded in this period, so there is nothing to show here yet.</p>
        <p><em>Extra work counts deals won in the Upsell pipeline, credited to whoever owned the deal in the month it was won.</em></p>
      </>
    );
  }
  const oneTimeShare = Math.round((L.upsellOneTimeUsd / total) * 100);
  const recurringShare = 100 - oneTimeShare;
  const avgDeal = total / L.upsellDeals;

  const on = months.filter((m) => m.onBook);
  const myPlaced = on.reduce((a, m) => a + m.placedUsd, 0);
  const myUp = on.reduce((a, m) => a + m.upsellRecurringUsd + m.upsellOneTimeUsd, 0);
  const restPlaced = on.reduce((a, m) => a + m.restPlacedUsd, 0);
  const restUp = on.reduce((a, m) => a + m.restUpsellUsd, 0);
  const mine = myPlaced > 0 ? (myUp / myPlaced) * 100 : null;
  const theirs = restPlaced > 0 ? (restUp / restPlaced) * 100 : null;
  const ratio = mine != null && theirs != null && theirs > 0 ? mine / theirs : null;
  const span = on.length ? `${fmtMonth(on[0].month)} to ${fmtMonth(on[on.length - 1].month)}` : '';

  const sellingMonths = on.filter((m) => m.upsellRecurringUsd + m.upsellOneTimeUsd > 0).length;
  const share = Math.round((sellingMonths / Math.max(1, on.length)) * 100);
  const top5 = L.upsellTop5Pct;

  return (
    <>
      <h4>What this shows</h4>
      <p>
        {pm} sold <b>{fmtMoney(total)}</b> of extra work in <b>{plural(L.upsellDeals, 'deal')}</b> — an average of{' '}
        <b>{fmtMoney(avgDeal)}</b> per deal.
      </p>
      <p>
        <b>{oneTimeShare}%</b> of that is <b>One Time Upsell</b>, paid once only. <b>{recurringShare}%</b> is{' '}
        <b>Recurring Upsell</b>, which the client pays every month.{' '}
        {recurringShare >= 40
          ? 'A big repeating share is the valuable kind, because the client keeps paying it every month from now on.'
          : 'Money paid once is over when it arrives. Only the repeating part means the client pays more every month from now on.'}
      </p>
      {mine != null && theirs != null && (
        <p>
          From <b>{span}</b>, for every <b>$1</b> of client money {pm} looked after, they sold{' '}
          <b>{Math.round(mine)}c</b> of extra work. Every other PM sold <b>{Math.round(theirs)}c</b> over those same months
          {ratio != null && ratio >= 1.15 && <> — about <ToneText $tone="good"><b>{ratio.toFixed(1)} times the company rate</b></ToneText></>}
          {ratio != null && ratio <= 0.85 && <> — about <ToneText $tone="bad"><b>{Math.round((1 - ratio) * 100)}% below the company rate</b></ToneText></>}
          . Comparing it this way is fair to everyone: a PM with a few clients and a PM with many can be
          judged side by side.
        </p>
      )}
      <p>
        Extra work was sold in <b>{sellingMonths} of {plural(on.length, 'month')}</b> ({share}%)
        {top5 != null && <>, to <b>{plural(L.upsellDistinctClients, 'different client')}</b>. The five biggest deals
          make up <b>{Math.round(top5)}%</b> of the money</>}.{' '}
        {top5 != null && top5 >= 40
          ? 'That is a lot to rest on a few deals: losing one of them would take a large piece out of the total.'
          : share >= 60
            ? 'Selling in most months, to many different clients, is a habit rather than luck. That is what makes the number repeatable.'
            : 'Plenty of clients, but the sales landed in only some of the months.'}
      </p>
    </>
  );
}

const toneForPct = (p: number | null): 'good' | 'bad' | 'plain' => (p == null ? 'plain' : p >= 90 ? 'good' : p < 80 ? 'bad' : 'plain');

const MyPerformancePage: React.FC = () => {
  const theme = useTheme() as any;
  const [pm, setPm] = useState<string | undefined>(undefined);
  const [data, setData] = useState<MyPerfData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false); // [LOAD-STATES 2026-09-29] a 403 is final, no countdown
  const [showAllMoves, setShowAllMoves] = useState(false);

  /* [TAB-SPEED 2026-09-10] The PM whose record is on screen. The first load asks for no PM and the
     server picks one; putting that name into `pm` used to re-run the effect below and fetch the
     SAME record a second time, skeleton back up (~2-3 s each on prod). That echo is now skipped. */
  const shownFor = React.useRef<string | undefined>(undefined);
  const load = useCallback(async (who?: string) => {
    setLoading(true); setError(null);
    try {
      const d = await fetchMyPerformance(who);
      setData(d);
      shownFor.current = d.pm ?? undefined;
      if (d.pm && d.pm !== who) setPm(d.pm);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.response?.data?.error || 'Could not load this record.');
      setForbidden(isForbidden(e)); // [LOAD-STATES 2026-09-29]
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (pm !== undefined && pm === shownFor.current) return;
    load(pm);
  }, [load, pm]);

  const months = useMemo(() => data?.months ?? [], [data]);
  const s = data?.summary;
  /* The month `summary.now` describes — the server builds it from the last month in the list, so
     this is the same row and needs no extra request. Used for the upsell breakdown. */
  /* The base for the table's data bars: the biggest month in each column. Guarded at 1 so a PM
     with an empty book cannot divide by zero. */
  const maxPlaced = useMemo(() => Math.max(...months.map((m) => m.placedUsd), 1), [months]);
  const maxRetained = useMemo(() => Math.max(...months.map((m) => m.retainedUsd), 1), [months]);
  /* The same month a year before the one on the tiles — the fair comparison for a seasonal book.
     Undefined when the PM was not on the book then, and every delta simply disappears. */
  const vd = useMemo(() => (s ? verdict(data?.pm ?? '', months, s) : { good: [], bad: [] }), [data, months, s]);
  const yoy = useMemo(() => yearOnYear(months), [months]);
  const yearAgo = useMemo(() => {
    const m = s?.now?.month;
    if (!m) return undefined;
    const prev = `${Number(m.slice(0, 4)) - 1}-${m.slice(5)}`;
    const found = months.find((x) => x.month === prev);
    return found && found.onBook ? found : undefined;
  }, [months, s]);
  const nowMonth = useMemo(() => (s?.now ? months.find((m) => m.month === s.now!.month) ?? null : null), [months, s]);

  const rangeLabel = data?.range ? `${fmtMonth(data.range.from)} – ${fmtMonth(data.range.to)}` : '';

  return (
    <Container>
      <PageHead>
        <TitleLine>
          <PageTitle>Performance Review</PageTitle>
          <TitleRule aria-hidden="true" />
          <PageSub>
            One project manager&apos;s record, month by month, from their first month of joining to the last
            complete month.
          </PageSub>
        </TitleLine>
        {/* [MY-PERF 2026-09-02, user] The months sit to the LEFT of the PM picker, which stays hard
            right — the picker is the control you reach for, the months are context for it. */}
        <HeadRight>
          {data?.range && s && (
            <WindowPill>{rangeLabel} <Small>· {plural(s.monthsInRange, 'month')}</Small></WindowPill>
          )}
          {data?.canPickOthers && data.roster.length > 0 ? (
            <PickWrap>
              <GlassSelect liquid /* [LIQUID-GLASS 2026-11-02] */ deferChange /* [FLUID-8 2026-10-12] instant pick */ value={data.pm ?? ''} onChange={(e: any) => setPm(e.target.value)}>
                {data.roster.map((n) => <option key={n} value={n}>{n}</option>)}
              </GlassSelect>
            </PickWrap>
          ) : data?.pm ? (
            <WindowPill><i className="fas fa-id-card" aria-hidden="true" /> {data.pm}</WindowPill>
          ) : null}
          {/* [PDF-EXPORT 2026-09-30, user] No "Download PDF" on this tab: removed on request (EOM keeps its own). */}
        </HeadRight>
      </PageHead>

      {loading && <LoadingShape />}

      {/* [LOAD-STATES 2026-09-29, user] Was the raw server text ("Internal server error"). */}
      {!loading && error && (forbidden ? <ErrorBox>{error}</ErrorBox> : <LoadFailed what="this record" onRetry={() => load(pm)} />)}

      {!loading && !error && data && !data.pm && (
        <Card>
          <Empty>
            {data.reason === 'not_a_current_pm'
              ? 'This page shows a project manager\'s own record. Your account is not on the current PM roster, so there is nothing to show here.'
              : 'Nothing to show.'}
          </Empty>
        </Card>
      )}

      {!loading && !error && data && data.pm && s && (
        <>
          {/* 01 — where they stand as of the last COMPLETE month (not "today": the current month is excluded) */}
          <Section>
            <Eyebrow>Section 01</Eyebrow>
            <SectionHead>
              <SectionTitle>Where {data.pm} stands</SectionTitle>
              <TitleRule aria-hidden="true" />
              {/* Why the current month is missing is said once, in the notes at the foot of the
                  page, rather than repeated in every section heading. */}
              <SectionSub>
                {s.now ? <>As per the last complete month, {fmtMonth(s.now.month)}.</> : <>No complete month on record yet.</>}
              </SectionSub>
            </SectionHead>
            {s.now ? (
              <Grid $min={172}>
                <Tile>
                  <TileLabel>Clients</TileLabel>
                  <TileValue>{s.now.clients}</TileValue>
                  <TileSub>Recurring Clients{deltaChip(s.now.clients, yearAgo?.clients, 'money')}</TileSub>
                </Tile>
                <Tile>
                  <TileLabel>Inventory</TileLabel>
                  <TileValue>{fmtMoney(s.now.placedUsd)}</TileValue>
                  <TileSub>Expected from those clients{deltaChip(s.now.placedUsd, yearAgo?.placedUsd, 'money')}</TileSub>
                </Tile>
                <Tile>
                  <TileLabel>Retained</TileLabel>
                  <TileValue $tone={toneForPct(s.now.retentionPct)}>{fmtPct(s.now.retentionPct)}</TileValue>
                  <TileSub>{fmtMoney(s.now.retainedUsd)} actually arrived{deltaChip(s.now.retentionPct, yearAgo?.retentionPct, 'points')}</TileSub>
                </Tile>
                <Tile>
                  <TileLabel>Upsell</TileLabel>
                  <TileValue>{fmtMoney(s.now.upsellUsd)}</TileValue>
                  <TileSub>
                    Extra business sold that month
                    {nowMonth && upsellSplit(nowMonth.upsellOneTimeUsd, nowMonth.upsellRecurringUsd)
                      ? ` - ${upsellSplit(nowMonth.upsellOneTimeUsd, nowMonth.upsellRecurringUsd)}`
                      : ''}
                    {deltaChip(s.now.upsellUsd, yearAgo ? yearAgo.upsellRecurringUsd + yearAgo.upsellOneTimeUsd : undefined, 'money')}
                  </TileSub>
                </Tile>
                <Tile>
                  <TileLabel>Money back out</TileLabel>
                  <TileValue $tone={s.now.moneyOutUsd > 0 ? 'bad' : 'plain'}>{fmtMoney(s.now.moneyOutUsd)}</TileValue>
                  <TileSub>Refunds and lost disputes{deltaChip(s.now.moneyOutUsd, yearAgo ? yearAgo.refundsUsd + yearAgo.disputesLostUsd : undefined, 'money', true)}</TileSub>
                </Tile>
              </Grid>
            ) : <Card><Empty>No complete month yet.</Empty></Card>}
            {yearAgo && (
              <SectionSub style={{ marginTop: 10 }}>
                Each change is against <b>{fmtMonth(yearAgo.month)}</b> — the same month a year earlier, so a
                seasonal book is compared like for like.
              </SectionSub>
            )}
            {nowMonth && <Standing>{standingNarrative(data.pm, nowMonth)}</Standing>}

            <div style={{ height: 12 }} />
            <SectionSub>Since {fmtMonth(s.firstMonth)} — {plural(s.monthsOnBook, 'month')} with clients on the book.</SectionSub>
            <Grid $min={172}>
              <Tile>
                <TileLabel>Inventory, all time</TileLabel>
                <TileValue>{fmtMoney(s.lifetime.placedUsd)}</TileValue>
                <TileSub>Added up across every month</TileSub>
              </Tile>
              <Tile>
                <TileLabel>Retained, all time</TileLabel>
                <TileValue $tone={toneForPct(s.lifetime.retentionPct)}>{fmtPct(s.lifetime.retentionPct)}</TileValue>
                <TileSub>{fmtMoney(s.lifetime.retainedUsd)} of the inventory arrived</TileSub>
              </Tile>
              <Tile>
                <TileLabel>Upsell, all time</TileLabel>
                <TileValue>{fmtMoney(s.lifetime.upsellRecurringUsd + s.lifetime.upsellOneTimeUsd)}</TileValue>
                <TileSub>
                  {plural(s.lifetime.upsellDeals, 'deal')}
                  {upsellSplit(s.lifetime.upsellOneTimeUsd, s.lifetime.upsellRecurringUsd)
                    ? ` · ${upsellSplit(s.lifetime.upsellOneTimeUsd, s.lifetime.upsellRecurringUsd)}`
                    : ''}
                </TileSub>
              </Tile>
              <Tile>
                <TileLabel>Clients in / out</TileLabel>
                <TileValue>{s.lifetime.clientsArrived} <Small style={{ fontSize: 13 }}>in</Small> · {s.lifetime.clientsLeft} <Small style={{ fontSize: 13 }}>out</Small></TileValue>
                <TileSub>Arrived on / left this PM&apos;s book</TileSub>
              </Tile>
              <Tile>
                <TileLabel>Cents per $1</TileLabel>
                <TileValue $tone={s.lifetime.bankedPer100 == null ? 'plain' : s.lifetime.bankedPer100 >= 100 ? 'good' : s.lifetime.bankedPer100 < 85 ? 'bad' : 'plain'}>
                  {s.lifetime.bankedPer100 == null ? '—' : `${Math.round(s.lifetime.bankedPer100)}¢`}
                </TileValue>
                <TileSub>Came in for every $1 of inventory, after refunds</TileSub>
              </Tile>
            </Grid>
            <Standing>{lifetimeNarrative(data.pm, s, months)}</Standing>
          </Section>

          {/* 02 — the four measures the page is built from, in plain English */}
          <Section>
            <Eyebrow>Section 02</Eyebrow>
            {/* Wrapped so the gap before the cards matches Section 01, whose SectionHead
                supplies it; without a SectionSub the bare title left only 3px. */}
            <SectionHead><SectionTitle>What these terms mean</SectionTitle></SectionHead>
            <Card>
              <Terms>
                <div><dt>Inventory</dt><dd>The money a PM&apos;s clients are expected to pay in a month — every recurring client&apos;s monthly amount added up. The size of the book they are responsible for.</dd></div>
                <div><dt>Retention</dt><dd>How much of that expected money actually arrived, in dollars and as a percentage. 100% means every client paid in full.</dd></div>
                <div><dt>Upsell</dt><dd>Extra business sold to a client the company already has. Recurring Upsell repeats every month; One Time Upsell is paid once.</dd></div>
                <div><dt>Money back out</dt><dd>Refunds and lost disputes — money that came in and then had to be given back. Disputes the company won stay in the bank.</dd></div>
                <div><dt>Current Net In Bank</dt><dd>What is actually in the bank for the month: retention, plus all upsell and any disputes won, minus refunds and lost disputes.</dd></div>
              </Terms>
            </Card>
          </Section>

          {/* 03 — the journey */}
          <Section>
            <Eyebrow>Section 03</Eyebrow>
            <SectionHead>
              <SectionTitle>The journey — {fmtMonth(s.firstMonth)} to {fmtMonth(s.lastMonth)}</SectionTitle>
              <TitleRule aria-hidden="true" />
              <SectionSub>
                Outer bar is inventory, inner bar what was retained, % above how much arrived. Lines are upsell and disputes won; refunds sit below the line.
              </SectionSub>
            </SectionHead>
            {yoy && (
              <Card style={{ marginBottom: 14 }}>
                <SectionSub style={{ marginBottom: 10 }}>
                  <b>{yoy.curYear} against {yoy.prevYear}</b> — {yoy.label} both years, so the two are
                  like for like and a part-finished year is not compared with a whole one.
                </SectionSub>
                <Scroll>
                  <YoY>
                    <thead>
                      <tr>
                        <th>Measure</th>
                        <th>{yoy.label} {yoy.prevYear}</th>
                        <th>{yoy.label} {yoy.curYear}</th>
                        <th>Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yoy.rows.map((r) => (
                        <tr key={r.metric}>
                          <td>{r.metric}</td>
                          <td>{r.prev}</td>
                          <td>{r.cur}</td>
                          <td>{r.delta ?? <Small>—</Small>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </YoY>
                </Scroll>
              </Card>
            )}
            <SplitRow>
              <Card>
                <JourneyChart months={months} />
                <ChartSummary>{journeySummary(data.pm, s)}</ChartSummary>
              </Card>
              <Insight>{journeyInsight(data.pm, months)}</Insight>
            </SplitRow>
          </Section>

          {/* 04 — upsell */}
          <Section>
            <Eyebrow>Section 04</Eyebrow>
            <SectionHead>
              <SectionTitle>Do they grow their accounts?</SectionTitle>
              <TitleRule aria-hidden="true" />
              <SectionSub>
              Each month&apos;s upsell, split by type — recurring keeps paying every month, one-time is paid once.
              {s.biggestUpsellMonth
                ? <> Biggest month: <b>{fmtMonth(s.biggestUpsellMonth.month)}</b> at {fmtMoney(s.biggestUpsellMonth.usd)}. All time: {fmtMoney(s.lifetime.upsellRecurringUsd + s.lifetime.upsellOneTimeUsd)} across {plural(s.lifetime.upsellDeals, 'deal')}.</>
                : <> No upsell deals on record in this period.</>}
              </SectionSub>
            </SectionHead>
            <SplitRow>
            <Card>
              {s.lifetime.upsellDeals === 0 ? <Empty>No upsell deals closed by {data.pm} between {fmtMonth(s.firstMonth)} and {fmtMonth(s.lastMonth)}.</Empty> : (
                <MonthChart
                  months={months.map((m) => m.month)}
                  bars={months.map((m) => m.upsellRecurringUsd)}
                  bars2={months.map((m) => m.upsellOneTimeUsd)}
                  gaps={months.map(() => false)}
                  barLabel="Recurring Upsell"
                  bar2Label="One Time Upsell"
                  barColor={theme.greenInk}
                  bar2Color={BLUE}
                  tip={(i) => [
                    { label: 'Recurring Upsell', value: fmtMoney(months[i].upsellRecurringUsd) },
                    { label: 'One Time Upsell', value: fmtMoney(months[i].upsellOneTimeUsd) },
                    { label: 'Deals', value: String(months[i].upsellDeals) },
                  ]}
                />
              )}
            </Card>
            <Insight>{upsellInsight(data.pm, months, s)}</Insight>
            </SplitRow>
          </Section>

          {/* 05 — every month in one table */}
          <Section>
            <Eyebrow>Section 05</Eyebrow>
            <SectionHead>
              <SectionTitle>Every month, in one table</SectionTitle>
              <TitleRule aria-hidden="true" />
              <SectionSub>Every figure on this page, month by month, with the newest month first — for checking a single month or copying the numbers out.</SectionSub>
            </SectionHead>
            <Card $plain>
              <TableKey>
                <span><b>Retention %</b> is coloured by how much of the book arrived:</span>
                <span><ToneText $tone="good">90% or more</ToneText></span>
                <span><ToneText $tone="warn">80–90%</ToneText></span>
                <span><ToneText $tone="bad">under 80%</ToneText></span>
                <span>
                  <b>Everyone else %</b> is what every OTHER PM retained that month, this one left out, so a big
                  book is never compared against itself — <ToneText $tone="good">▲</ToneText> ahead of them,{' '}
                  <ToneText $tone="bad">▼</ToneText> behind, and <b>Gap</b> is that distance in points.
                </span>
              </TableKey>
              <Scroll>
                <TablePanel>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Month</Th>
                        <Th $right>Clients</Th>
                        <Th $right>Inventory</Th>
                        <Th $right>Retained</Th>
                        <Th $right>Retention %</Th>
                        <Th $right>Everyone else %</Th>
                        <Th $right>Gap</Th>
                        <Th $right>Recurring Upsell</Th>
                        <Th $right>One Time Upsell</Th>
                        <Th $right>Share of company upsell</Th>
                        <Th $right>Refund/Disputes</Th>
                        <Th $right>Disputes won</Th>
                        <Th $right>Current Net In Bank</Th>
                        <Th $right>¢ per $1</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {months.slice().reverse().map((m) => (
                        <tr key={m.month}>
                          <Td $strong>{fmtMonth(m.month)}</Td>
                          {!m.onBook ? (
                            <>
                              <Td $right $muted>—</Td><Td $right $muted>No clients</Td><Td $right $muted>—</Td><Td $right $muted>—</Td><Td $right $muted>—</Td><Td $right $muted>—</Td>
                            </>
                          ) : (
                            <>
                              <Td $right>{m.clients}</Td>
                              <Td $right><BarCell $pct={(m.placedUsd / maxPlaced) * 100} $tone="blue">{fmtMoney(m.placedUsd)}</BarCell></Td>
                              <Td $right><BarCell $pct={(m.retainedUsd / maxRetained) * 100} $tone="green">{fmtMoney(m.retainedUsd)}</BarCell></Td>
                              <Td $right><ToneText $tone={m.retentionPct == null ? 'muted' : m.retentionPct >= 90 ? 'good' : m.retentionPct < 80 ? 'bad' : 'warn'}>{fmtPct(m.retentionPct)}</ToneText></Td>
                              {/* The company's figure for the same month, with an arrow saying which side of it this
                                  PM landed: own above the average is a green ▲, below is a red ▼, level or unknown
                                  is just the number. The figure itself stays in plain ink — it is the benchmark. */}
                              <Td $right>
                                {m.avgRetentionPct == null ? <Small>—</Small> : (
                                  <>
                                    {fmtPct(m.avgRetentionPct)}
                                    {m.retentionPct != null && m.retentionPct > m.avgRetentionPct && <ToneText $tone="good"> ▲</ToneText>}
                                    {m.retentionPct != null && m.retentionPct < m.avgRetentionPct && <ToneText $tone="bad"> ▼</ToneText>}
                                  </>
                                )}
                              </Td>
                              {/* The gap as a NUMBER, not just an arrow: "13.3 points behind" is a
                                  size; an arrow is only a direction. */}
                              <Td $right>
                                {m.retentionPct == null || m.avgRetentionPct == null ? <Small>—</Small> : (
                                  <ToneText $tone={m.retentionPct >= m.avgRetentionPct ? 'good' : 'bad'}>
                                    {m.retentionPct >= m.avgRetentionPct ? '+' : ''}{(m.retentionPct - m.avgRetentionPct).toFixed(1)}
                                  </ToneText>
                                )}
                              </Td>
                            </>
                          )}
                          <Td $right>{m.upsellRecurringUsd ? fmtMoney(m.upsellRecurringUsd) : <Small>—</Small>}</Td>
                          <Td $right>{m.upsellOneTimeUsd ? fmtMoney(m.upsellOneTimeUsd) : <Small>—</Small>}</Td>
                          {/* How much of everything the company sold that month came from this PM. */}
                          <Td $right>
                            {m.companyUpsellUsd > 0 && (m.upsellRecurringUsd + m.upsellOneTimeUsd) > 0
                              ? fmtPct((100 * (m.upsellRecurringUsd + m.upsellOneTimeUsd)) / m.companyUpsellUsd)
                              : <Small>—</Small>}
                          </Td>
                          {/* refunds + lost disputes, the pair My Inventory calls refundsDisputes */}
                          <Td $right>{m.refundsUsd + m.disputesLostUsd > 0 ? <ToneText $tone="bad">{fmtMoney(m.refundsUsd + m.disputesLostUsd)}</ToneText> : <Small>—</Small>}</Td>
                          <Td $right>{m.disputesWonUsd ? <ToneText $tone="good">{fmtMoney(m.disputesWonUsd)}</ToneText> : <Small>—</Small>}</Td>
                          {/* The same figure My Inventory's KPI strip calls Current Net In Bank. */}
                          <Td $right $strong>{m.onBook || m.currentNetInBankUsd !== 0
                            ? (m.currentNetInBankUsd < 0
                              ? <ToneText $tone="bad">{fmtMoney(m.currentNetInBankUsd)}</ToneText>
                              : fmtMoney(m.currentNetInBankUsd))
                            : <Small>—</Small>}</Td>
                          <Td $right>{m.bankedPer100 == null
                            ? <Small>—</Small>
                            : m.bankedPer100 < 0
                              ? <ToneText $tone="bad">{`${Math.round(m.bankedPer100)}¢`}</ToneText>
                              : `${Math.round(m.bankedPer100)}¢`}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TablePanel>
              </Scroll>
              <Standing>{tableSummary(data.pm, months)}</Standing>
            </Card>
          </Section>

          {/* 06 — the closing read */}
          <Section>
            <Eyebrow>Section 06</Eyebrow>
            <SectionHead>
              <SectionTitle>Where {data.pm} stands, in short</SectionTitle>
              <TitleRule aria-hidden="true" />
              <SectionSub>Every line below is a figure from this page, not an opinion.</SectionSub>
            </SectionHead>
            <Card>
              <Verdict>
                <div>
                  <h4 className="good">What is working</h4>
                  {vd.good.length
                    ? <ul>{vd.good.map((x, i) => <li key={i}>{x}</li>)}</ul>
                    : <p className="none">Not enough history yet to call anything out.</p>}
                </div>
                <div>
                  <h4 className="bad">What to watch</h4>
                  {vd.bad.length
                    ? <ul>{vd.bad.map((x, i) => <li key={i}>{x}</li>)}</ul>
                    : <p className="none">Nothing on this page falls below the mark.</p>}
                </div>
              </Verdict>
            </Card>
          </Section>

          {/* 07 — where the figures come from, kept short on purpose */}
          <Section>
            <Eyebrow>Section 07</Eyebrow>
            <SectionHead>
              <SectionTitle>How these numbers are built</SectionTitle>
              <TitleRule aria-hidden="true" />
              <SectionSub>The short version, so any figure here can be checked against another tab.</SectionSub>
            </SectionHead>
            <Card>
              <Method>
                <li><b>Inventory and Retention</b> come from the inventory table, recurring clients only — money received divided by money expected, weighted by dollars. The same rule the dashboard uses.</li>
                <li><b>Upsell</b> is closed-won deals in the Upsell pipeline, credited to the deal owner in the month the deal closed.</li>
                <li><b>Refunds and disputes</b> are counted against the PM the client belonged to that month.</li>
                <li><b>Everyone else %</b> is the same retention sum over every other PM&apos;s rows, this PM removed, so a large book is never compared with itself.</li>
                <li><b>Current Net In Bank</b> is retention + upsell + disputes won − refunds − lost disputes, the same figure My Inventory shows.</li>
                <li>The page runs from <b>{fmtMonth(s.firstMonth as string)}</b> to the <b>last complete month</b>; the month in progress is never shown, because a part-month always looks like a collapse.</li>
              </Method>
            </Card>
          </Section>
        </>
      )}
    </Container>
  );
};


export default MyPerformancePage;
